import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAgeStage, formatAge } from "../../../utils/ageUtils.js";
import Pill from "../../../shared/Pill.js";
import {
  translateSize,
  translateAdjective,
} from "../../../utils/translateUtils.js";
import { LikePill } from "../../../shared/Icons/LikePill.js";
import { useWishlistContext } from "../../../context/WishlistProvider.js";

interface Animal {
  id: string;
  name: string;
  type: "cao" | "gato";
  breed?: string;
  size?: "PEQUENO" | "MEDIO" | "GRANDE";
  gender?: "MACHO" | "FEMEA";
  age?: number;
  photo_url?: string;
  image?: string;
  adjectives?: string;
  likes?: number;
  viewerHasWishlisted?: boolean;
  // Dog-specific fields
  energy_level?: "baixo" | "medio" | "alto";
  training_level?: "nenhum" | "basico" | "intermediario" | "avancado";
  good_with_kids?: boolean;
  good_with_other_dogs?: boolean;
  good_with_cats?: boolean;
  // Cat-specific fields
  playfulness?: "baixo" | "medio" | "alto";
  purringLevel?: "baixo" | "medio" | "alto";
  indoorOnly?: boolean;
}

type CardPosition = "far-left" | "left" | "center" | "right" | "far-right";

interface AnimalCarrouselCardProps {
  animal: Animal;
  position: CardPosition;
  active: boolean;
  onClick: () => void;
  likeCount?: number; // Prop to display like count
}

export const AnimalCarrouselCard: React.FC<AnimalCarrouselCardProps> = ({
  animal,
  position,
  active,
  onClick,
  likeCount = 0,
}) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { isInWishlist, toggleWishlist, loading } = useWishlistContext();
  const contextLiked = isInWishlist(animal.id, animal.type);
  const isLiked =
    typeof animal.viewerHasWishlisted === "boolean"
      ? animal.viewerHasWishlisted || contextLiked
      : contextLiked;
  const [currentLikeCount, setCurrentLikeCount] = useState(likeCount);

  const getCardClass = () => {
    let classes = `absolute top-0 left-1/2 rounded-2xl cursor-pointer
                      transform-gpu transition-all duration-300 ease-in-out
                      bg-landing-gradient bg-cover bg-center bg-no-repeat overflow-hidden
                      border-4 border-white shadow-2xl
                      h-[480px] w-[340px] `;
    // decorative scallop frame
    classes += " scallop";
    // Add group class for center card so child can scale on hover
    if (position === "center") {
      classes += " group";
    }

    return classes;
  };

  const getCardStyle = () => {
    let transform = "";
    let zIndex = 0;
    let opacity = 1;

    // Position-based styles with 3D transforms
    if (position === "far-left") {
      transform =
        "translateX(-165%) translateY(6%) scale(0.65) rotateY(-35deg) rotateZ(-10deg)";
      zIndex = 0;
      opacity = 0.7;
    } else if (position === "left") {
      transform =
        "translateX(-120%) translateY(3%) scale(0.8) rotateY(-20deg) rotateZ(-4deg)";
      zIndex = 10;
      opacity = 0.85;
    } else if (position === "center") {
      transform = "translateX(-50%) scale(1) rotateY(0deg) rotateZ(0deg)";
      zIndex = 20;
      opacity = 1;
    } else if (position === "right") {
      transform =
        "translateX(20%) translateY(3%) scale(0.8) rotateY(20deg) rotateZ(4deg)";
      zIndex = 10;
      opacity = 0.85;
    } else if (position === "far-right") {
      transform =
        "translateX(65%) translateY(6%) scale(0.65) rotateY(35deg) rotateZ(10deg)";
      zIndex = 0;
      opacity = 0.7;
    }

    return {
      transform,
      zIndex,
      opacity,
      transformStyle: "preserve-3d" as const,
    };
  };

  // Background image string for inner wrapper (so it can scale)
  const backgroundImageStyle = animal.photo_url
    ? `url(${animal.photo_url})`
    : animal.image
      ? `url(${animal.image})`
      : undefined;

  const handleClick = () => {
    try {
      navigate(`/${animal.type}/${animal.name}-${animal.id}`);
    } catch (err) {
      console.error("Navigation error:", err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const handleHeartClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const res = await toggleWishlist(animal.id, animal.type, animal.name);
    if (res && typeof res.likes === "number") {
      setCurrentLikeCount(res.likes);
    }
  };

  return (
    <div
      className={getCardClass()}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      style={getCardStyle()}
    >
      {/* Inner wrapper scales on hover for center card */}
      <div
        className={`absolute inset-0 transition-transform duration-300 will-change-transform ${
          position === "center"
            ? "group-hover:scale-[101%] hover:scale-[101%]"
            : ""
        } rounded-2xl bg-cover bg-center bg-no-repeat overflow-hidden`}
        style={{ backgroundImage: backgroundImageStyle }}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Heart icon in top right */}
        <div className="absolute top-4 right-4 z-50">
          <LikePill
            liked={isLiked}
            count={currentLikeCount}
            onClick={handleHeartClick}
            disabled={loading}
            loading={loading}
          />
        </div>

        {/* Card content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
          <p className="uppercase text-xs font-medium tracking-wide text-white/80 mb-1">
            {animal.gender === "MACHO"
              ? t("animal.male")
              : animal.gender === "FEMEA"
                ? t("animal.female")
                : animal.gender}
          </p>
          <h2 className="text-xl font-bold text-white mb-2">{animal.name}</h2>

          {/* Show details only when active (center) */}
          {position === "center" && (
            <div className="flex flex-wrap gap-2 mt-3">
              {animal.size && (
                <Pill variant="green" size="xs">
                  {translateSize(animal.size)}
                </Pill>
              )}
              {animal.age && (
                <Pill variant="purple" size="xs">
                  {getAgeStage(animal.age)}
                </Pill>
              )}
              {animal.adjectives && (
                <Pill variant="blue" size="xs">
                  {Array.isArray(animal.adjectives)
                    ? translateAdjective(animal.adjectives[0])
                    : typeof animal.adjectives === "string"
                      ? translateAdjective(
                          animal.adjectives.split(",")[0]?.trim(),
                        )
                      : ""}
                </Pill>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnimalCarrouselCard;
